<script setup lang="ts">
/**
 * PAGE CAGNOTTE — l'association en bas de page, centrée (façon Dift) : logo, nom
 * vérifié, thème, présentation, 3 chiffres, « Faire un don », réseaux, et
 * ses autres cagnottes (jamais celles d'autres assos).
 */
import { themeAffiche } from "~/utils/themesAffiches";
import PageCagnotteReseaux from "./PageCagnotteReseaux.vue";
import type { AssociationCagnotte } from "./types";

const props = defineProps<{
  association: AssociationCagnotte;
  slugCagnotte: string;
  /** Donateurs de CETTE cagnotte : plancher du total de l'asso. */
  donateursCagnotte: number;
  /** Couleur de la page de paiement de l'asso (comme les boutons rapides). */
  couleur?: string | null;
  /** Cagnotte terminée / en pause / brouillon : pas de « Faire un don ». */
  donsFermes?: boolean;
}>();

/** « Faire un don » : même couleur que les boutons rapides de montant. */
const styleBoutonDon = computed(() => ({
  background: props.couleur
    ? props.couleur
    : "var(--color-main)",
}));
const emit = defineEmits<{ donner: [] }>();
const { formatCurrency } = useFormatLangue();
const { t } = useI18n();
const localePath = useLocalePath();

const theme = computed(() =>
  props.association.category && props.association.category !== "autre" ? themeAffiche(props.association.category) : "",
);
const paragraphes = computed(() =>
  (props.association.description ?? "").split(/\n+/).map((p) => p.trim()).filter(Boolean),
);
const chiffres = computed(() => {
  const a = props.association;
  return [
    { valeur: formatCurrency(Number(a.total_raised) || 0, { currency: "EUR", decimals: 0 }), libelle: t("campagne.pageCagnotte.collectesSurDonoor") },
    { valeur: String(Math.max(Number(a.total_donors) || 0, props.donateursCagnotte)), libelle: t("campagne.pageCagnotte.donateurs", Math.max(Number(a.total_donors) || 0, props.donateursCagnotte)) },
    { valeur: String(Number(a.campaigns_total) || 0), libelle: t("campagne.pageCagnotte.cagnottes", Number(a.campaigns_total) || 0) },
  ];
});
const autresCagnottes = computed(() =>
  (props.association.campaigns ?? []).filter((c) => c.slug !== props.slugCagnotte).slice(0, 3),
);
</script>

<template>
  <section id="association" class="scroll-mt-24 pt-20 lg:pt-28 text-center">
    <div class="mx-auto w-28 h-28 rounded-[28px] bg-white shadow-[0_10px_30px_-10px_rgba(12,48,151,0.3)] ring-1 ring-black/5 flex items-center justify-center overflow-hidden">
      <OrgLogo :logo="association.logo" :name="association.name" size-class="w-24 h-24" rounded-class="rounded-2xl" ring-class="" :width="240" />
    </div>
    <p class="mt-6 inline-flex items-center gap-2 text-[22px] text-(--color-ink)" style="font-weight: 700 !important">
      {{ $t("campagne.pageCagnotte.associationTitre", { nom: association.name }) }}
      <UIcon v-if="association.is_verified" name="i-lucide-badge-check" class="size-6 text-[#1f9afe]" />
    </p>
    <div v-if="theme || association.address_city" class="mt-5 flex flex-wrap justify-center gap-3">
      <span v-if="theme" class="inline-flex items-center gap-2 px-5 py-2.5 rounded-full ring-1 ring-gray-200 text-[15px] text-(--color-ink)" style="font-weight: 600 !important">
        <UIcon name="i-lucide-heart-handshake" class="size-4" /> {{ theme }}
      </span>
      <span v-if="association.address_city" class="inline-flex items-center gap-2 px-5 py-2.5 rounded-full ring-1 ring-gray-200 text-[15px] text-(--color-ink)" style="font-weight: 600 !important">
        <UIcon name="i-lucide-map-pin" class="size-4" /> {{ association.address_city }}
      </span>
    </div>

    <div v-if="paragraphes.length" class="mt-7 mx-auto max-w-4xl space-y-4">
      <p v-for="(p, i) in paragraphes" :key="i" class="text-[16px] leading-relaxed text-gray-500">{{ p }}</p>
    </div>

    <div class="mt-12 grid sm:grid-cols-3 gap-8">
      <div v-for="c in chiffres" :key="c.libelle">
        <p class="font-display text-[44px] sm:text-[52px] leading-none text-(--color-ink)">{{ c.valeur }}</p>
        <p class="mt-3 text-[16px] text-gray-500">{{ c.libelle }}</p>
      </div>
    </div>

    <!-- Bouton, réseaux, lien : chacun sur sa ligne, centrés — avec ou sans
         réseaux renseignés (sans, le lien se collait au bouton). -->
    <div class="mt-12 flex flex-col items-center gap-8">
      <button
        v-if="!donsFermes"
        type="button"
        class="inline-flex items-center gap-2 px-12 py-3.5 rounded-full text-white transition-[filter] duration-200 hover:brightness-95"
        :style="styleBoutonDon"
        style="font-weight: 700 !important"
        @click="emit('donner')"
      >
        <UIcon name="i-lucide-heart" class="size-5" />
        {{ $t("campagne.pageCagnotte.faireUnDon") }}
      </button>

      <PageCagnotteReseaux :liens="association.social_links" :site="association.website" centre />

      <a
        :href="localePath({ path: `/associations/${association.slug}` })"
        class="inline-flex items-center gap-1.5 text-[15px] text-(--color-ink) underline underline-offset-4 decoration-gray-300 hover:decoration-(--color-ink)"
        style="font-weight: 500 !important"
      >{{ $t("campagne.pageCagnotte.voirPageAsso") }} <UIcon name="i-lucide-arrow-right" class="size-4" /></a>
    </div>

    <div v-if="autresCagnottes.length" class="mt-16 text-left">
      <p class="text-xl text-(--color-ink)" style="font-weight: 700 !important">{{ $t("campagne.pageCagnotte.autresCagnottes") }}</p>
      <div class="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <RechercheCagnotteCard
          v-for="c in autresCagnottes"
          :key="String(c.id)"
          :campaign="{ ...(c as any), organization_name: association.name, organization_logo: association.logo }"
        />
      </div>
    </div>
  </section>
</template>
